import {
    AccountBalance as AccountBalanceIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Folder as FolderIcon,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React, { useContext, useEffect } from "react";

import { KycContext } from "../../contexts/kyc/KycContext";
import { IKycContextApplicationFragment } from "../../contexts/kyc/KycContext.generated";
import { IApplicationStatus } from "../../generated/types";
import { useInterval } from "../../hooks/useInterval";
import { Routes } from "../../Routes";
import { EVENTS, Segment } from "../../services/segment";
import { logNever } from "../../utils/assertNever";
import { ApplicationDocuments } from "./documents/ApplicationDocuments";

const POLLING_INTERVAL_MS = 1000;

interface IProps {
    isLoading: boolean;
    isError: boolean | undefined;
    application: IKycContextApplicationFragment | undefined;
}

const ApplicationLoading: React.FC = () => (
    <Stack alignItems="center">
        <CircularProgress />
        <Typography align="center" mt={1}>
            Submitting your application. Please wait...
        </Typography>
    </Stack>
);

const ApplicationPending: React.FC = () => {
    const { refetchApplication } = useContext(KycContext);
    useInterval(refetchApplication, POLLING_INTERVAL_MS);

    return (
        <Stack alignItems="center">
            <CircularProgress />
            <Typography align="center" mt={1}>
                Reviewing your application. This should take no more than 60 seconds.
            </Typography>
        </Stack>
    );
};

const ApplicationDenied: React.FC = () => {
    return (
        <Stack alignItems="center" spacing={1}>
            <ErrorIcon color="disabled" sx={{ fontSize: 60 }} />
            <Typography fontWeight="bold" align="center">
                Application Denied
            </Typography>
            <Typography align="justify">
                Unfortunately, we are unable to approve your application at this time due to certain limitations and
                restrictions with our banking partners.
            </Typography>
            <Typography align="justify">
                We are constantly improving and adding support for new currencies and customer use cases, and will reach
                out when we can support you. We hope to serve your business in the future.
            </Typography>
        </Stack>
    );
};

const ApplicationPendingReview: React.FC = () => {
    return (
        <Stack alignItems="center">
            <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
            <Typography fontWeight="bold" align="center" mt={1}>
                Application Pending Review
            </Typography>
            <Typography align="justify" mt={3}>
                Thank you for submitting your application! We are excited to have you on board. We will review your
                application within the next 24-48 hours and get back to you via email. Reach out to us at{" "}
                <Link href="mailto:support@levro.com?subject=Signup%20Help" target="_blank">
                    support@levro.com
                </Link>{" "}
                in the meantime if you have questions.
            </Typography>
        </Stack>
    );
};

const ApplicationApproved: React.FC = () => {
    const { unitApplication, refetchApplication } = useContext(KycContext);
    const router = useRouter();

    const isAccountReady = unitApplication?.status === IApplicationStatus.AccountSetup;
    useInterval(refetchApplication, isAccountReady ? undefined : POLLING_INTERVAL_MS);

    const handleGoToAccount = () => {
        Segment.track(EVENTS.APPLY_CLICK_GO_TO_MY_ACCOUNT);
        router.push(Routes.Dashboard());
    };

    return (
        <Stack alignItems="center" spacing={4}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
            <Typography fontWeight="bold" align="center">
                Application Approved
            </Typography>
            <Typography align="center">Your application was approved! Welcome to Levro.</Typography>
            {isAccountReady ? (
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleGoToAccount}
                    startIcon={<AccountBalanceIcon />}
                >
                    Go to my account
                </Button>
            ) : (
                <Button
                    variant="outlined"
                    loading={true}
                    loadingPosition="start"
                    startIcon={<Box sx={{ width: 20 }} />}
                >
                    Setting up your account...
                </Button>
            )}
        </Stack>
    );
};

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1_000_000;

const ApplicationAwaitingDocuments: React.FC<{ application: IKycContextApplicationFragment }> = ({
    application: { id, documents },
}) => {
    return (
        <Stack alignItems="center" spacing={3}>
            <FolderIcon color="warning" sx={{ fontSize: 60 }} />
            <Typography fontWeight="bold">Action Required</Typography>
            <Stack spacing={1} textAlign="justify">
                <Typography>
                    Your application was saved, but we need some additional documents to verify your information. Please
                    proceed below to upload documents and complete your application.
                </Typography>
                <Typography variant="label1">Note that the maximum file size is {MAX_FILE_SIZE_MB}mb.</Typography>
            </Stack>
            <ApplicationDocuments applicationId={id} documents={documents} maxFileSizeBytes={MAX_FILE_SIZE_BYTES} />
        </Stack>
    );
};

export const ApplicationStatusPage: React.FC<{ application: IKycContextApplicationFragment }> = ({ application }) => {
    const { status } = application;

    switch (status) {
        case IApplicationStatus.New:
            return <React.Fragment />; // This should never happen, just appeasing TypeScript.
        case IApplicationStatus.AwaitingDocuments:
            return <ApplicationAwaitingDocuments application={application} />;
        case IApplicationStatus.Pending:
            return <ApplicationPending />;
        case IApplicationStatus.PendingReview:
        case IApplicationStatus.SubmissionError:
        case IApplicationStatus.Canceled:
            return <ApplicationPendingReview />;
        case IApplicationStatus.Denied:
            return <ApplicationDenied />;
        case IApplicationStatus.AccountSetup:
        case IApplicationStatus.Approved:
            return <ApplicationApproved />;
        default:
            return logNever(status, null);
    }
};

export const FinalizeApplication: React.FC<IProps> = ({ isError, isLoading, application }) => {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        if (!isLoading) {
            Segment.track(EVENTS.APPLY_APPLICATION_RESULT, {
                result: application ? application.status : isError ? "UnknownError" : undefined,
            });
        }
    }, [isError, isLoading, application]);

    return (
        <>
            {isLoading && <ApplicationLoading />}
            {isError && <ApplicationPendingReview />}
            {application != null && <ApplicationStatusPage application={application} />}
        </>
    );
};
